// The pharmacy panel's look, from the P1–P3 designs (designs/pharmacy):
// mint-grey canvas, white cards on a light line, deep teal for actions,
// pill-shaped controls 44 px tall or more, Inter for text and Manrope for the
// wordmark. Labels and helpers shared by the queue and the order card.

import { useEffect, useState } from "react";

import type { QueueLine, QueueOrder } from "../api";

export const C = {
  canvas: "#f3f7f6",
  surface: "#ffffff",
  tint: "#e9f2ef",
  line: "#e3e9e7",
  ink: "#0e1716",
  body: "#2c3532",
  muted: "#5c6562",
  teal: "#0f806e",
  onTeal: "#ffffff",
  priceStrong: "#1d8049",
  genericLine: "#b4d3c4",
  danger: "#c0392b",
} as const;

export const font = { sans: 'Inter, "Noto Sans Bengali", system-ui, sans-serif', display: "Manrope, Inter, sans-serif" };

/** A pill button in the design's two weights: teal (primary) or white with a line (secondary). */
export function pill(kind: "primary" | "secondary", height = 44): React.CSSProperties {
  return {
    height,
    padding: "0 20px",
    borderRadius: 999,
    border: kind === "primary" ? "none" : `1px solid ${C.line}`,
    background: kind === "primary" ? C.teal : C.surface,
    color: kind === "primary" ? C.onTeal : C.ink,
    fontSize: 15,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

export const card: React.CSSProperties = {
  background: C.surface,
  border: `1px solid ${C.line}`,
  borderRadius: 16,
};

export const eyebrow: React.CSSProperties = { fontSize: 11, lineHeight: "14px", fontWeight: 600, letterSpacing: "0.12em", color: C.muted };

/** Tablets and capsules come in strips of 10; bottles, tubes and the like are one each. */
export const unitsPerStrip = (form: string) => (/tablet|capsule|caplet/i.test(form) ? 10 : 1);

/** "mirpur-10" → "Mirpur 10". */
export function areaLabel(area: string | null, fallback: string): string {
  const key = area ?? fallback;
  return key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "Today by 9 PM", "Tomorrow by 12 PM", or a date. Dhaka time. */
export function slotLabel(deliverBy: string | null): string {
  if (!deliverBy) return "Next delivery";
  const at = new Date(deliverBy);
  const day = (d: Date) => d.toLocaleDateString("en-GB", { timeZone: "Asia/Dhaka" });
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86_400_000);
  const time = at.toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka", hour: "numeric", minute: at.getMinutes() ? "2-digit" : undefined });
  if (day(at) === day(now)) return `Today by ${time}`;
  if (day(at) === day(tomorrow)) return `Tomorrow by ${time}`;
  return `${at.toLocaleDateString("en-US", { timeZone: "Asia/Dhaka", weekday: "short", month: "short", day: "numeric" })} by ${time}`;
}

export const payLabel = (method: string) => (method === "cod" ? "Cash on delivery" : "Paid online");

/** "Seclo 20 mg × 6 strips · Napa Extra × 2 strips". */
export function itemsSummary(lines: QueueLine[]): string {
  return lines
    .map((l) => `${l.medicine ? [l.medicine.brandName, l.medicine.strength].filter(Boolean).join(" ") : `Medicine #${l.medicineId}`} × ${l.strips} strip${l.strips === 1 ? "" : "s"}`)
    .join(" · ");
}

/** Re-renders every second, for countdowns. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** "Accept in 07:42", or "Late by 03:10" once the 10 minutes have passed. */
export function acceptTimer(o: Pick<QueueOrder, "acceptBy">, now: number): { text: string; late: boolean; soon: boolean } | null {
  if (!o.acceptBy) return null;
  const left = Math.round((Date.parse(o.acceptBy) - now) / 1000);
  const abs = Math.abs(left);
  const mmss = `${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  return left >= 0 ? { text: `Accept in ${mmss}`, late: false, soon: left < 180 } : { text: `Late by ${mmss}`, late: true, soon: true };
}
